import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { 
  UserPlus, Edit, Trash2, KeyRound, Shield, Clock, Search, Users
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_CONFIG = {
  owner: { label: "Owner", color: "bg-purple-100 text-purple-700" },
  manager: { label: "Manager", color: "bg-blue-100 text-blue-700" },
  cashier: { label: "Cashier", color: "bg-green-100 text-green-700" },
  kitchen: { label: "Kitchen", color: "bg-orange-100 text-orange-700" },
  waiter: { label: "Waiter", color: "bg-teal-100 text-teal-700" },
};

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cashier" });
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const r = await axios.get(`${API}/restaurant/staff`);
      setStaff(r.data);
    } catch (e) { toast.error("Failed to load staff"); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill all required fields"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/restaurant/staff`, form);
      setStaff([...staff, r.data]);
      toast.success("Staff created!");
      setCreateDialog(false);
      setForm({ name: "", email: "", password: "", role: "cashier" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create staff"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!selectedStaff) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/restaurant/staff/${selectedStaff.id}`, {
        name: form.name, email: form.email, role: form.role, is_active: form.is_active
      });
      setStaff(staff.map(s => s.id === selectedStaff.id ? r.data : s));
      toast.success("Staff updated!");
      setEditDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!selectedStaff || !newPassword) return;
    setSaving(true);
    try {
      await axios.post(`${API}/restaurant/staff/${selectedStaff.id}/reset-password`, { new_password: newPassword });
      toast.success("Password reset!");
      setResetDialog(false); setNewPassword("");
    } catch (e) { toast.error("Failed to reset password"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return;
    try {
      await axios.delete(`${API}/restaurant/staff/${staffId}`);
      setStaff(staff.filter(s => s.id !== staffId));
      toast.success("Staff deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const handleToggleActive = async (staffMember) => {
    try {
      const r = await axios.put(`${API}/restaurant/staff/${staffMember.id}`, { is_active: !staffMember.is_active });
      setStaff(staff.map(s => s.id === staffMember.id ? r.data : s));
      toast.success(r.data.is_active ? "Staff activated" : "Staff suspended");
    } catch (e) { toast.error("Failed to update"); }
  };

  const openEdit = (s) => {
    setSelectedStaff(s);
    setForm({ name: s.name, email: s.email, role: s.role, is_active: s.is_active !== false });
    setEditDialog(true);
  };

  const filtered = staff.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase()) || s.role?.includes(search.toLowerCase())
  );

  const statCounts = {
    total: staff.length,
    active: staff.filter(s => s.is_active !== false).length,
    suspended: staff.filter(s => s.is_active === false).length,
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
    <div className="space-y-6" data-testid="staff-management">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Staff Management</h1>
        <p className="text-sm text-muted-foreground">Create and manage your team members</p>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-foreground">{statCounts.total}</p>
          <p className="text-sm text-muted-foreground">Total Staff</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{statCounts.active}</p>
          <p className="text-sm text-muted-foreground">Active</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{statCounts.suspended}</p>
          <p className="text-sm text-muted-foreground">Suspended</p>
        </CardContent></Card>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search staff..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="staff-search" />
        </div>
        <Button onClick={() => { setForm({ name: "", email: "", password: "", role: "cashier" }); setCreateDialog(true); }} data-testid="add-staff-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Staff
        </Button>
      </div>

      {/* Staff List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <Card className="border-dashed"><CardContent className="flex flex-col items-center py-12">
            <Users className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <p className="font-semibold text-lg mb-1">No staff found</p>
            <p className="text-muted-foreground text-sm">Add your first team member</p>
          </CardContent></Card>
        ) : filtered.map(s => (
          <Card key={s.id} className={cn("transition-all", s.is_active === false && "opacity-60")} data-testid={`staff-${s.id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-primary font-bold text-sm">{s.name?.[0]?.toUpperCase() || "?"}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-semibold text-foreground truncate">{s.name}</p>
                  <Badge className={cn("text-xs", ROLE_CONFIG[s.role]?.color || "bg-gray-100 text-gray-700")}>
                    {ROLE_CONFIG[s.role]?.label || s.role}
                  </Badge>
                  {s.is_active === false && <Badge className="bg-red-100 text-red-700 text-xs">Suspended</Badge>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{s.email}</p>
                {s.last_login && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Clock className="w-3 h-3" /> Last login: {new Date(s.last_login).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={s.is_active !== false} onCheckedChange={() => handleToggleActive(s)} className="data-[state=checked]:bg-green-500" />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedStaff(s); setNewPassword(""); setResetDialog(true); }}>
                  <KeyRound className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(s.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ahmed Hassan" data-testid="staff-name-input" /></div>
            <div className="space-y-2"><Label>Email *</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ahmed@dalabsom.com" data-testid="staff-email-input" /></div>
            <div className="space-y-2"><Label>Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password" data-testid="staff-password-input" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  <SelectItem value="kitchen">Kitchen</SelectItem>
                  <SelectItem value="waiter">Waiter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={handleCreate} disabled={saving} data-testid="create-staff-submit">
              {saving ? "Creating..." : "Create Staff Member"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Staff</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  <SelectItem value="kitchen">Kitchen</SelectItem>
                  <SelectItem value="waiter">Waiter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={handleUpdate} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password for {selectedStaff?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" data-testid="reset-password-input" />
            </div>
            <Button className="w-full" onClick={handleResetPassword} disabled={saving || !newPassword}>
              {saving ? "Resetting..." : "Reset Password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </AdminLayout>
  );
};

export default StaffManagement;
